<template>
  <div>
    <v-card class="pa-4">
      <v-form @submit.prevent="submitFilters">
        <v-row class="statistic-header">
          <v-col cols="12" sm="12" md="4" lg="2">
            <date-time-picker
              v-model="fromDate"
              :textFieldProps="{ ...textFieldProps, required: true, errorMessages: fromDateErrorMessages }"
              :datePickerProps="{ max: datePickerMaxDate }"
              :label="'From Date (GMT+' + getGmtDiff + ')'">
            </date-time-picker>
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="2">
            <date-time-picker
              v-model="untilDate"
              :textFieldProps="{ ...textFieldProps, required: true, errorMessages: untilDateErrorMessages }"
              :label="'Until Date (GMT+' + getGmtDiff + ')'"
              :datePickerProps="{ max: untilDatePickerMaxDate }">
            </date-time-picker>
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="2">
            <v-select
              outlined
              v-model="selectedValues"
              :items="tenants"
              item-text="name"
              class="font-weight-bold tenant-select"
              item-value="id"
              :menu-props="{ maxHeight: '400' }"
              label="Select tenants"
              multiple
              single-line
              hide-details
              chips>
              <template v-slot:item="{ item, attrs, parent }">
                <v-list-item-action>
                  <v-simple-checkbox
                    color="primary"
                    :value="attrs.inputValue"
                    :ripple="false"
                    @input="parent.$emit('select', item)" />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action
                  v-if="tenants.length > 1 && selectedValues && selectedValues.length > 0"
                  class="tenant-only-action">
                  <v-btn small text color="primary" @mousedown.stop.prevent @click.stop="selectOnlyTenant(item.id)">
                    Only
                  </v-btn>
                </v-list-item-action>
              </template>

              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index < 1" small>
                  <span class="text-truncate">{{ item.name }}</span>
                </v-chip>
                <span v-if="index === 1" class="grey--text text-caption"> (+{{ selectedValues.length - 1 }}) </span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="12" md="6" lg="2">
            <v-select
              v-model="event.status"
              :items="eventStatus"
              item-text="label"
              item-value="value"
              placeholder="Filter by Claim Events"
              outlined
              hide-details
              clearable></v-select>
          </v-col>
          <!-- Upload External Reports Section -->
          <v-col cols="12" sm="12" md="6" lg="4">
            <v-btn class="wrapping-btn" style="height: 56px; width: 100%" @click="uploadDialog = true">
              <v-icon style="margin-right: 5px">mdi-file-upload-outline</v-icon>
              Upload External Reports to Match (Optional)
            </v-btn>
            <v-dialog v-model="uploadDialog" max-width="600px">
              <v-card>
                <v-card-title class="headline">Upload external reports</v-card-title>
                <v-card-text>
                  <!-- Upload Tora Report -->
                  <div class="mb-4">
                    <file-uploader-mini
                      :multiple="false"
                      :text="'Upload Tora report (Optional)'"
                      ref="fileUploaderTora" />
                  </div>
                  <!-- Upload SilverRail Report -->
                  <div class="mb-4">
                    <file-uploader-mini
                      :multiple="false"
                      :text="'Upload SilverRail report (Optional)'"
                      ref="fileUploaderSilverRail" />
                  </div>
                  <!-- Upload Swish Report -->
                  <div>
                    <file-uploader-mini
                      :multiple="false"
                      :text="'Upload Swish report (Optional)'"
                      ref="fileUploaderSwish" />
                  </div>
                  <div class="mt-6">
                    <v-checkbox
                      v-model="matchWithKustom"
                      label="Include external Kustom payment data (optional)"
                      hide-details />

                    <div class="text-caption grey--text mt-1 ml-8">
                      Kustom data is fetched automatically, so no report upload is needed.
                    </div>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="uploadDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
          <v-col cols="12" md="6" lg="8" class="d-flex align-center flex-wrap" style="gap: 8px">
            <span class="text-caption grey--text text--darken-1 mr-1">Quick ranges</span>
            <v-chip
              v-for="preset in datePresets"
              :key="preset.key"
              :color="preset.key === activeDatePreset ? 'primary' : undefined"
              :outlined="preset.key !== activeDatePreset"
              @click="applyDatePreset(preset)">
              {{ preset.label }}
            </v-chip>
          </v-col>
          <!-- Same width as the upload column above, split equally between the two buttons -->
          <v-col cols="12" md="6" lg="4" class="d-flex" style="gap: 10px">
            <v-tooltip bottom :disabled="!disabledReason">
              <template v-slot:activator="{ on, attrs }">
                <span class="d-flex" style="flex: 1 1 0" v-bind="attrs" v-on="on">
                  <v-btn
                    dark
                    large
                    type="submit"
                    style="flex: 1 1 0"
                    hint="Get Statistics"
                    :loading="loading"
                    :disabled="!!disabledReason"
                    class="button accent text-button wrapping-btn px-3">
                    <v-icon style="margin-right: 5px">mdi-chart-bar</v-icon>
                    Get Statistics
                  </v-btn>
                </span>
              </template>
              <span>{{ disabledReason }}</span>
            </v-tooltip>
            <v-tooltip bottom :disabled="!disabledReason">
              <template v-slot:activator="{ on, attrs }">
                <span class="d-flex" style="flex: 1 1 0" v-bind="attrs" v-on="on">
                  <v-btn
                    dark
                    large
                    class="button greeny text-button wrapping-btn px-3"
                    style="flex: 1 1 0"
                    hint="Download Accounting Report"
                    :loading="loading"
                    :disabled="!!disabledReason"
                    @click="downloadAccountingReport()">
                    <v-icon style="margin-right: 5px"> mdi-microsoft-excel </v-icon>
                    Download Report
                  </v-btn>
                </span>
              </template>
              <span>{{ disabledReason }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!-- No results for the submitted filters: one message instead of empty tiles, charts and order list -->
    <v-card v-if="showEmptyState" class="mt-6 px-6 py-10 text-center">
      <v-avatar color="grey lighten-3" size="56" class="mb-4">
        <v-icon color="grey darken-1">mdi-magnify-remove-outline</v-icon>
      </v-avatar>
      <div class="text-h6 font-weight-bold">No orders found in the selected date range.</div>
      <div class="text-body-2 grey--text text--darken-1 mt-2 mx-auto" style="max-width: 480px">
        Nothing matched these filters, so there are no statistics, charts or orders to show. Try a wider date range or
        different filters.
      </div>
    </v-card>

    <!-- Applies to both the summary tiles and the charts -->
    <div v-if="showResults" class="d-flex align-center justify-end flex-wrap mt-6">
      <span v-if="selectedCurrency === totalCurrency" class="text-caption grey--text text--darken-1 mr-3">
        Total combines all currencies, with amounts converted to SEK.
      </span>
      <v-btn-toggle
        v-model="selectedCurrency"
        class="currency-toggle"
        mandatory
        dense
        active-class="primary white--text">
        <v-btn :value="totalCurrency" class="px-4">Total</v-btn>
        <v-btn v-for="currency in currencyOptions" :key="currency" :value="currency" class="px-4">
          {{ currency }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Orders and amounts fill a row of their own (3 x 4 columns), so the item counts always start below them;
         on large screens all five item counts share one row -->
    <v-row v-if="showResults" class="mt-1">
      <v-col
        v-for="tile in summaryTiles"
        :key="tile.label"
        cols="12"
        :sm="tile.itemCount ? 6 : 4"
        md="4"
        :lg="tile.itemCount">
        <v-card class="fill-height px-4 py-3">
          <v-skeleton-loader v-if="loading" type="text, heading, text" class="summary-skeleton" />
          <template v-else>
            <div class="text-subtitle-2 grey--text text--darken-1">{{ tile.label }}</div>
            <div class="text-h5 font-weight-bold my-1">{{ tile.value }}</div>
            <div class="text-caption grey--text text--darken-1">{{ tile.caption }}</div>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="showResults" class="my-7 pa-4">
      <v-row>
        <v-col cols="12">
          <v-tabs v-model="chartTab">
            <v-tab v-for="group in chartGroups" :key="group.key" :tab-value="group.key">
              {{ group.label }} Chart
            </v-tab>
          </v-tabs>
          <v-divider class="mb-4"></v-divider>
          <v-tabs-items v-model="chartTab">
            <v-tab-item v-for="group in chartGroups" :key="group.key" :value="group.key">
              <v-row v-if="loading">
                <v-col v-for="n in 4" :key="n" cols="12" md="6">
                  <v-skeleton-loader type="heading, image" class="chart-skeleton"></v-skeleton-loader>
                </v-col>
              </v-row>
              <div v-else-if="!charts[group.key].length" class="text-center grey--text py-12">No data available</div>
              <v-row v-else>
                <v-col v-for="chart in charts[group.key]" :key="chart.name" cols="12" md="6">
                  <div class="d-flex align-center text-subtitle-2 font-weight-bold">
                    {{ chart.name }}
                    <v-tooltip v-if="chartNotes[chart.name]" bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ chartNotes[chart.name] }}</span>
                    </v-tooltip>
                  </div>
                  <apexchart
                    :key="chartsVersion"
                    type="line"
                    height="250"
                    :options="chart.options"
                    :series="chart.series"></apexchart>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-card>
    <!-- v-show, not v-if: remounting the table would emit update:options and trigger an extra table fetch -->
    <v-card v-show="showResults" class="my-7 pa-4">
      <div class="d-flex align-center justify-space-between pb-3">
        <div class="text-h6 font-weight-bold">Orders</div>
        <!-- Both export every order of the query on screen, not just the current page -->
        <div class="d-flex">
          <export-excel :fetch="fetchAllOrders" :fields="orderExportFields" :name="orderExportName">
            <v-btn elevation="2" color="greeny" dark small :loading="exportingOrders" :disabled="!canExportOrders">
              <v-icon> mdi-microsoft-excel</v-icon>
            </v-btn>
          </export-excel>

          <!-- The library swaps the .xls in the name for .csv -->
          <export-excel :fetch="fetchAllOrders" :fields="orderExportFields" :name="orderExportName" type="csv">
            <v-btn
              elevation="2"
              dark
              color="greeny"
              small
              class="ml-3"
              :loading="exportingOrders"
              :disabled="!canExportOrders">
              <v-icon dark> mdi-table-large</v-icon>
            </v-btn>
          </export-excel>
        </div>
      </div>
      <v-data-table
        :headers="headers"
        :items="orders.items"
        :loading="loading || tableLoading"
        :server-items-length="orders.total"
        :options="filter"
        @update:options="onTableOptionsUpdate"
        :footer-props="{
          'items-per-page-options': [10, 20, 50, 100, 250, 500],
        }">
        <template v-slot:[`item.id`]="{ item }">
          <OrderIDField :order="item"></OrderIDField>
        </template>

        <!-- Cell styles below mirror the order list on the Search Orders page -->
        <template v-slot:[`item.externalBookingNumbers`]="{ item }">
          <div v-for="(bookingNumber, i) in bookingNumbers(item)" :key="i">
            <h4 class="grey--text text--darken-4 d-flex text-no-wrap">
              <v-tooltip top :open-delay="10" :close-delay="5">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="mango-red" small v-bind="attrs" v-on="on" @click="copyToClipboard(bookingNumber)">
                    mdi-content-copy
                  </v-icon>
                </template>
                <span>Copy</span>
              </v-tooltip>
              <span class="ml-1 font-weight-bold">{{ bookingNumber }}</span>
            </h4>
            <v-divider v-if="i + 1 !== bookingNumbers(item).length"></v-divider>
          </div>
          <span v-if="!bookingNumbers(item).length" class="grey--text text--darken-2 font-weight-bold">—</span>
        </template>

        <template v-slot:[`item.created`]="{ item }">
          <dateTimeField :date="item.created" type="dateTime"></dateTimeField>
        </template>

        <!-- Tenant is shown as its logo, with the name in the tooltip -->
        <template v-slot:[`item.soldBy`]="{ item }">
          <div class="d-flex align-center text-no-wrap font-weight-medium">
            <v-tooltip v-if="item.tenant" bottom color="black">
              <template v-slot:activator="{ on }">
                <v-avatar width="20" height="20" tile min-width="30" class="mr-1" v-on="on">
                  <img :src="tenantLogoUrl(item.tenant)" :alt="item.tenant" />
                </v-avatar>
              </template>
              <span>{{ item.tenant }}</span>
            </v-tooltip>
            <span>{{ item.soldBy }}</span>
          </div>
        </template>

        <template v-slot:[`item.price`]="{ item }">
          <span class="font-weight-bold text-no-wrap">{{ item.price?.plainText || "—" }}</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import DateTimePicker from "../../components/common/DateTimePicker.vue";
import { eventStatus } from "@/globals";
import FileUploaderMini from "@/components/media/FileUploaderMini.vue";
import OrderIDField from "@/components/common/OrderIDField";
import dateTimeField from "@/components/common/dateTimeField";
import common from "@/mixins/common";
import Trainplanet from "@/util/trainplanet.api";
import { fixUrl, getErrorMessage } from "@/util/helpers";

// Keyed by metric so the same metric keeps its color in every chart tab
// isAmount: tooltip values get the currency appended
const METRICS = {
  "Total Order Price": { color: "#2a78d6", decimals: 0, isAmount: true },
  "Total Order Count": { color: "#eb6834", decimals: 0 },
  "Average Order Price": { color: "#1baf7a", decimals: 0, isAmount: true },
  "Average Daily Order Count": { color: "#4a3aa7", decimals: 2 },
  "Average Hourly Order Count": { color: "#4a3aa7", decimals: 2 },
  "Average Minutely Order Count": { color: "#4a3aa7", decimals: 2 },
};

// The page's time zone: quick ranges, the order export and the statistics periods (sent as the timezone param)
const SWEDISH_TIMEZONE = "Europe/Stockholm";

// One chart tab per period list in the statistics response (key = its field there).
// period: how the response labels a period, in Swedish time, and the step from one period to the next. Hourly labels
// carry their UTC offset ("2026-10-14 14:00+02:00"), which tells apart the two 02:00 hours when the clocks go back.
// covered: the period field saying how much of it the date range covers, in units of which an hour has perHour.
// Datetime groups get the labels' Swedish time as UTC timestamps, so the datetime axis (datetimeUTC) shows it as
// sent; the offset is left out, so the two 02:00 hours share one spot on the axis.
const CHART_GROUPS = [
  {
    key: "monthly",
    label: "Monthly",
    period: { format: "YYYY-MM", unit: "month" },
    category: (period) => moment(period, "YYYY-MM").format("MMM YYYY"),
    averageCount: { name: "Average Daily Order Count", field: "averageDailyOrderCount" },
    covered: { field: "coveredDays", perHour: 1 / 24, units: "days", whole: "calendar month" },
  },
  {
    key: "daily",
    label: "Daily",
    period: { format: "YYYY-MM-DD", unit: "day" },
    category: (period) => moment.utc(period, "YYYY-MM-DD").valueOf(),
    averageCount: { name: "Average Hourly Order Count", field: "averageHourlyOrderCount" },
    covered: { field: "coveredHours", perHour: 1, units: "hours", whole: "day" },
    datetime: { tooltipFormat: "d MMM yyyy", exportFormat: "YYYY-MM-DD" },
  },
  {
    key: "hourly",
    label: "Hourly",
    period: { format: "YYYY-MM-DD HH:mmZ", unit: "hour" },
    category: (period) => moment.utc(period.slice(0, 16), "YYYY-MM-DD HH:mm").valueOf(),
    averageCount: { name: "Average Minutely Order Count", field: "averageMinutelyOrderCount" },
    covered: { field: "coveredMinutes", perHour: 60, units: "minutes", whole: "hour" },
    datetime: { tooltipFormat: "d MMM yyyy HH:mm", exportFormat: "YYYY-MM-DD HH:mm" },
  },
];

// A period label's start, as a timestamp
const periodStart = (label, { format }) => moment.tz(label, format, SWEDISH_TIMEZONE).valueOf();

// Every period label from the first to the last one, e.g. the days between "2026-09-19" and "2026-09-21".
// Stepped in Swedish time, so the night the clocks go forward has no 02:00 and the night they go back has two.
const periodRange = (first, last, { format, unit }) => {
  const labels = [];
  const end = moment.tz(last, format, SWEDISH_TIMEZONE);
  // isSameOrBefore is false for an invalid date, so the loop can't run forever
  for (const cursor = moment.tz(first, format, SWEDISH_TIMEZONE); cursor.isSameOrBefore(end); cursor.add(1, unit)) {
    labels.push(cursor.format(format));
  }
  return labels;
};

// The first and last period the date range touches; until is exclusive. It ends now at the latest: "This month"
// runs until tomorrow 00:00, and the hours still to come have no orders yet rather than none at all.
const rangePeriods = ({ fromDate, untilDate }, { format, unit }) => {
  const end = moment.min(moment(untilDate).subtract(1, "ms"), moment());
  // To the start of the period, e.g. a range from 10:30 starts in the 10:00 hour
  const period = (date) => moment.tz(date, SWEDISH_TIMEZONE).startOf(unit).format(format);
  return [period(fromDate), period(end)];
};

// A period's length in hours: a day is 23 or 25 hours, and its month as much shorter or longer, when the clocks change
const periodHours = (label, { format, unit }) => {
  const start = moment.tz(label, format, SWEDISH_TIMEZONE);
  return start.clone().add(1, unit).diff(start, "hours", true);
};

// A chart's y axis: from 0 up to a round maximum in at most 5 equal steps of 1, 2 or 5 times a power of 10, with
// labels just precise enough to tell the steps apart. Left to ApexCharts, an axis up to 0,02 got steps 0,004 apart,
// which 2 decimals print as "0,00 0,00 0,01 0,01 0,02 0,02". Counts and amounts (wholeSteps) only step in whole numbers.
const yAxisScale = (values, wholeSteps) => {
  const max = Math.max(0, ...values.filter((value) => Number.isFinite(value)));
  let step = 1;
  if (max > 0) {
    const rawStep = max / 5;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    step = [1, 2, 5, 10].map((multiple) => multiple * magnitude).find((candidate) => candidate >= rawStep);
  }
  if (wholeSteps) step = Math.max(1, step);
  const decimals = Math.max(0, -Math.floor(Math.log10(step)));
  // The small tolerance keeps float noise (0,02 / 0,005 = 4,000…01) from adding a step
  const steps = Math.max(1, Math.ceil(max / step - 1e-9));
  return { max: Number((steps * step).toFixed(decimals)), steps, decimals };
};

// Charts or raw periods per group key
const emptyByGroup = () => Object.fromEntries(CHART_GROUPS.map(({ key }) => [key, []]));

// "Total" view reads the period's top-level fields, which the backend already converts to SEK
const TOTAL_CURRENCY = "TOTAL";

// Quick date ranges, most used first. Boundaries are Swedish midnights and "until" is exclusive,
// e.g. yesterday = yesterday 00:00 → today 00:00 (Europe/Stockholm, DST-aware).
const DATE_PRESETS = [
  { key: "yesterday", label: "Yesterday", range: (today) => [today.clone().subtract(1, "day"), today] },
  {
    key: "lastMonth",
    label: "Last month",
    range: (today) => [today.clone().subtract(1, "month").startOf("month"), today.clone().startOf("month")],
  },
  {
    key: "thisMonth",
    label: "This month",
    range: (today) => [today.clone().startOf("month"), today.clone().add(1, "day")],
  },
  { key: "last7Days", label: "Last 7 days", range: (today) => [today.clone().subtract(7, "days"), today] },
  { key: "last30Days", label: "Last 30 days", range: (today) => [today.clone().subtract(30, "days"), today] },
  {
    key: "thisYear",
    label: "This year",
    range: (today) => [today.clone().startOf("year"), today.clone().add(1, "day")],
  },
];

const datePresetRange = (preset) => {
  const today = moment().tz(SWEDISH_TIMEZONE).startOf("day");
  const [from, until] = preset.range(today);
  return { fromDate: from.toDate(), untilDate: until.toDate() };
};

// Shared by the summary tiles and the charts. sv-SE matches the backend's plain texts ("22 329 SEK", "1 497,17 SEK").
// Null-safe: ApexCharts passes missing data points straight to its formatters, and a throw there breaks the tooltip.
const formatNumber = (value, decimals = 0, unit = "") => {
  const number = Number(value);
  if (value === null || value === undefined || !Number.isFinite(number)) return "—";
  const text = number.toLocaleString("sv-SE", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return unit ? `${text} ${unit}` : text;
};

// One currency order for the whole page (selection toggle and summary captions); others follow alphabetically
const CURRENCY_ORDER = ["SEK", "NOK", "DKK", "EUR"];
const compareCurrencies = (a, b) => {
  const rank = (currency) =>
    CURRENCY_ORDER.includes(currency) ? CURRENCY_ORDER.indexOf(currency) : CURRENCY_ORDER.length;
  return rank(a) - rank(b) || a.localeCompare(b);
};

// Summary tile caption: "All in SEK" when one currency contributes, otherwise "SEK 450 · EUR 1 · NOK 1"
const currencyBreakdown = (items, field, format) => {
  const contributing = items
    .filter((item) => item[field] > 0)
    .sort((a, b) => compareCurrencies(a.currency, b.currency));
  if (!contributing.length) return "—";
  if (contributing.length === 1) return `All in ${contributing[0].currency}`;
  return contributing.map(format).join(" · ");
};

// Summary tiles: orders and amounts first, then the item counts (marked itemCount, they get a row of their own)
const ORDERS_TILE = { label: "Orders", field: "orderCount" };
const ITEM_COUNT_TILES = [
  { label: "Journeys sold", field: "journeyCount" },
  { label: "Rail passes sold", field: "railPassCount" },
  { label: "Taxis sold", field: "taxiCount" },
  { label: "Gift cards sold", field: "giftCardCount" },
  { label: "Articles sold", field: "articleCount" },
];

// Total view: values are all-currency totals (amounts in SEK), captions break them down per currency
const totalSummaryTiles = (summary) => {
  const countTile = ({ label, field }) => ({
    label,
    value: formatNumber(summary[field]),
    caption: currencyBreakdown(summary.byCurrency, field, (item) => `${item.currency} ${formatNumber(item[field])}`),
  });

  return [
    countTile(ORDERS_TILE),
    {
      label: "Total amount",
      value: summary.totalAmountPlainText,
      caption: currencyBreakdown(summary.byCurrency, "totalAmount", (item) => item.totalAmountPlainText),
    },
    {
      label: "Average order amount",
      value: summary.averageOrderAmountPlainText,
      caption: currencyBreakdown(summary.byCurrency, "orderCount", (item) => item.averageOrderAmountPlainText),
    },
    ...ITEM_COUNT_TILES.map((tile) => ({ ...countTile(tile), itemCount: true })),
  ];
};

// Single-currency view: values come from that currency's byCurrency entry, captions relate them to the total
const currencySummaryTiles = (summary, currency) => {
  const stats = summary.byCurrency.find((item) => item.currency === currency);
  const orderCount = stats ? stats.orderCount : 0;
  const amountCaption = orderCount
    ? `From ${formatNumber(orderCount)} ${orderCount === 1 ? "order" : "orders"} in ${currency}`
    : `No orders in ${currency}`;
  const countTile = ({ label, field }) => ({
    label,
    value: formatNumber(stats ? stats[field] : 0),
    caption: `of ${formatNumber(summary[field])} across all currencies`,
  });

  return [
    countTile(ORDERS_TILE),
    { label: "Total amount", value: stats ? stats.totalAmountPlainText : "—", caption: amountCaption },
    { label: "Average order amount", value: stats ? stats.averageOrderAmountPlainText : "—", caption: amountCaption },
    ...ITEM_COUNT_TILES.map((tile) => ({ ...countTile(tile), itemCount: true })),
  ];
};

// Normalised: the table is always rendered (v-show), so a payload without orders must not throw
const normalizeOrders = (orders) => ({ items: orders?.items || [], total: orders?.meta?.total || 0 });

// Order export columns, as header: field
const ORDER_EXPORT_FIELDS = {
  "Order Id": "id",
  // The API sends UTC without a zone marker; Swedish time matches the date range and the file name
  "Created (Swedish time)": {
    field: "created",
    callback: (created) => (created ? moment.utc(created).tz(SWEDISH_TIMEZONE).format("YYYY-MM-DD HH:mm:ss") : ""),
  },
  "Booking IDs": "externalBookingNumbers",
  Tenant: "tenant",
  "Sold by": "soldBy",
  Journeys: "journeyCount",
  "Rail Passes": "railPassCount",
  Taxis: "taxiCount",
  "Gift Cards": "giftCardCount",
  Articles: "articleCount",
  Amount: "amount",
  Currency: "currency",
};

// e.g. "Statistics_Orders_SEK_2026-01-01_2026-10-13.xls". Days are Swedish, like the quick ranges; until is
// exclusive, so the name shows the last day the range covers.
const buildOrderExportName = ({ fromDate, untilDate }, currency) => {
  const day = (date) => moment(date).tz(SWEDISH_TIMEZONE).format("YYYY-MM-DD");
  const label = currency === TOTAL_CURRENCY ? "Total" : currency;
  return `Statistics_Orders_${label}_${day(fromDate)}_${day(moment(untilDate).subtract(1, "ms"))}.xls`;
};

export default {
  components: {
    FileUploaderMini,
    DateTimePicker,
    OrderIDField,
    dateTimeField,
  },
  mixins: [common],

  data: () => ({
    uploadDialog: false,
    matchWithKustom: false,

    chartGroups: CHART_GROUPS,
    charts: emptyByGroup(),
    // Charts are remounted (never updated) on every rebuild: ApexCharts' updateOptions on a grouped chart applies
    // the options, chart.id included, to every chart in the group. That leaks colors/formatters and leaves
    // destroyed charts registered under the wrong id, which breaks the synced tooltip ("reading 'ttItems'").
    // The version in the chart id keeps the new charts from colliding with the ones being destroyed.
    chartsVersion: 0,
    rawStats: emptyByGroup(),
    chartTab: "daily",
    currencyOptions: [],
    totalCurrency: TOTAL_CURRENCY,
    selectedCurrency: TOTAL_CURRENCY,
    event: {
      status: "",
    },
    eventStatus: eventStatus,
    headers: [
      {
        text: "Order Id",
        value: "id",
        sortable: false,
      },
      {
        text: "Booking IDs",
        value: "externalBookingNumbers",
        sortable: false,
      },
      {
        text: "Created",
        value: "created",
        // The list is paged by the backend, which isn't sent a sort, so sorting here would only refetch the same page
        sortable: false,
      },
      {
        text: "Sold by",
        value: "soldBy",
        sortable: false,
      },
      {
        text: "Total Price",
        value: "price",
        align: "end",
        sortable: false,
      },
    ],
    loading: false,
    tableLoading: false,
    exportingOrders: false,
    // Set when an export starts, see fetchAllOrders
    orderExportName: "",
    tableOptionsReady: false,
    // Filters of the query on screen; only replaced once a query succeeds, so a failed one leaves paging on the old results
    appliedFilters: null,
    // Filters of the submitted query that is still loading
    pendingFilters: null,
    // Every query request takes the next id; responses of all but the latest are dropped
    latestRequestId: 0,
    // Same for the order list, which page and currency changes fetch on their own
    latestOrdersRequestId: 0,
    orders: {
      items: [],
      total: 0,
    },
    // Set after the first successful query; the empty state is only shown once a query has actually returned
    hasLoaded: false,
    filter: {
      page: 1,
      itemsPerPage: 10,
    },
    textFieldProps: {
      outlined: true,
      "hide-details": "auto",
    },
    datePresets: DATE_PRESETS,
    orderExportFields: ORDER_EXPORT_FIELDS,
    // Defaults to yesterday, Swedish time
    ...datePresetRange(DATE_PRESETS[0]),
    selectedValues: [],
  }),
  watch: {
    tenants(val) {
      if (val.length > 0) {
        this.selectedValues = this.tenants.map((item) => item.id);
      }
    },
    selectedCurrency() {
      this.rebuildCharts();
      // The order list is filtered by the selected currency, so it's fetched again from the first page
      this.filter.page = 1;
      this.fetchOrders();
    },
  },
  mounted() {
    this.selectedValues = this.tenants.map((item) => item.id);
    this.initStatistics();
    this.initCurrencyOptions();
  },
  computed: {
    ...mapGetters({
      statistics: "statistics/statistics",
      tenants: "auth/userTenants",
    }),
    // The preset whose range matches the picked dates, so its chip shows as selected (also after manual picking)
    activeDatePreset() {
      if (!this.fromDate || !this.untilDate) return null;
      const match = DATE_PRESETS.find((preset) => {
        const { fromDate, untilDate } = datePresetRange(preset);
        return moment(this.fromDate).isSame(fromDate, "minute") && moment(this.untilDate).isSame(untilDate, "minute");
      });
      return match ? match.key : null;
    },
    // Results area (summary, charts, order list) is shown while loading and when there is data
    showResults() {
      return this.loading || this.hasStatistics;
    },
    // Only after a successful fetch, so a failed request (already reported via the error toast) isn't shown as "no orders"
    showEmptyState() {
      return !this.loading && !this.hasStatistics && this.hasLoaded;
    },
    // Normalised so a payload without summary/byCurrency renders the empty state instead of throwing
    summary() {
      const summary = this.statistics.summary || {};
      return { ...summary, byCurrency: summary.byCurrency || [] };
    },
    // The table page on screen
    pageParams() {
      return { limit: this.filter.itemsPerPage, offset: (this.filter.page - 1) * this.filter.itemsPerPage };
    },
    // The export sizes its request by the list's total, so it waits until the list on screen has loaded
    canExportOrders() {
      return !this.loading && !this.tableLoading && !!this.appliedFilters && this.orders.total > 0;
    },
    hasStatistics() {
      const { summary } = this;
      return !!(
        summary.orderCount ||
        summary.journeyCount ||
        summary.railPassCount ||
        summary.taxiCount ||
        summary.giftCardCount ||
        summary.articleCount
      );
    },
    summaryTiles() {
      const { summary } = this;
      return this.selectedCurrency === TOTAL_CURRENCY
        ? totalSummaryTiles(summary)
        : currencySummaryTiles(summary, this.selectedCurrency);
    },
    getGmtDiff() {
      return moment().utcOffset() / 60;
    },
    datePickerMaxDate() {
      return moment().format("YYYY-MM-DD");
    },
    // Until is exclusive, so covering all of today (e.g. "This month") needs tomorrow 00:00
    untilDatePickerMaxDate() {
      return moment().add(1, "day").format("YYYY-MM-DD");
    },
    fromDateErrorMessages() {
      return this.fromDate ? [] : ["From Date field cannot be empty"];
    },
    untilDateErrorMessages() {
      return this.untilDate ? [] : ["Until Date field cannot be empty"];
    },
    // Until is exclusive, so equal dates (e.g. both "9 Oct 00:00") would be an empty range that can never match
    isDateRangeValid() {
      return moment(this.fromDate).isBefore(this.untilDate);
    },
    // A query covers at most a year, e.g. 15 Oct 2025 00:00 until 15 Oct 2026 00:00
    isDateRangeWithinLimit() {
      return moment(this.untilDate).isSameOrBefore(moment(this.fromDate).add(1, "year"));
    },
    disabledReason() {
      if (!this.fromDate) {
        return "Please fill the From Date.";
      }
      if (!this.untilDate) {
        return "Please fill the Until Date.";
      }
      if (!this.isDateRangeValid) {
        return "Until Date must be later than From Date.";
      }
      if (!this.isDateRangeWithinLimit) {
        return "The date range can be at most 1 year.";
      }
      return "";
    },
    // Info tooltips next to chart titles, keyed by chart name; shown when the date range covers a month, day or hour
    // only partly. Measured against the period's real length, so a 23-hour day (clocks forward) doesn't count as
    // partly covered; 0.01 allows for the backend rounding its covered values.
    chartNotes() {
      const notes = {};
      CHART_GROUPS.forEach(({ key, period, averageCount, covered }) => {
        const partly = this.rawStats[key].some(
          (row) => row[covered.field] < periodHours(row.period, period) * covered.perHour - 0.01
        );
        if (partly) {
          notes[averageCount.name] =
            `Calculated using only the ${covered.units} covered by the selected date range, ` +
            `not the full ${covered.whole}.`;
        }
      });
      return notes;
    },
  },
  methods: {
    // Also accepts a single booking number, so a string is never iterated character by character
    bookingNumbers(order) {
      return [].concat(order.externalBookingNumbers || []);
    },
    tenantLogoUrl(tenantName) {
      return `https://assets.trainplanet.com/wlabel/${fixUrl(tenantName)}/logos/favicon-32x32.png`;
    },
    buildChartSeries(periods, { period: periodFormat, category, averageCount }) {
      // Periods without orders are missing from the response, and so is the selected currency's entry in periods
      // where it had none. A line would join their neighbours straight across, as if there were orders in between,
      // so every period of the date range is shown, those without orders as such.
      const statsByPeriod = new Map();
      periods.forEach((period) => {
        const stats = this.getPeriodStats(period);
        if (stats) statsByPeriod.set(period.period, stats);
      });
      const labels = [...statsByPeriod.keys()];
      // Only filled when every label reads back in the expected format; otherwise the filled labels could miss
      // the real ones, so the periods are shown as they came (sorted as text). With no orders at all, the charts'
      // "No data available" message is shown rather than flat zero lines.
      const canFill = labels.length && labels.every((label) => moment(label, periodFormat.format, true).isValid());
      let rows = labels.sort();
      if (canFill) {
        // By time: as text, "02:00+01:00" would sort before "02:00+02:00", which comes first. Each label is parsed
        // once, as the sort compares them many times.
        const starts = new Map(labels.map((label) => [label, periodStart(label, periodFormat)]));
        labels.sort((a, b) => starts.get(a) - starts.get(b));
        let [first, last] = [labels[0], labels[labels.length - 1]];
        // Out to the ends of the date range on screen
        if (this.appliedFilters) {
          const [rangeFirst, rangeLast] = rangePeriods(this.appliedFilters, periodFormat);
          if (periodStart(rangeFirst, periodFormat) < starts.get(first)) first = rangeFirst;
          if (periodStart(rangeLast, periodFormat) > starts.get(last)) last = rangeLast;
        }
        rows = periodRange(first, last, periodFormat);
      }
      rows = rows.map((label) => ({ label, stats: statsByPeriod.get(label) }));

      // Periods without orders show 0 in every chart, the average order price included
      const values = (field) => rows.map(({ stats }) => (stats ? stats[field] : 0));
      return {
        categories: rows.map(({ label }) => category(label)),
        series: [
          { name: "Total Order Price", data: values("totalAmount") },
          { name: "Total Order Count", data: values("orderCount") },
          { name: "Average Order Price", data: values("averageOrderAmount") },
          { name: averageCount.name, data: values(averageCount.field) },
        ],
      };
    },
    getPeriodStats(period) {
      if (this.selectedCurrency === TOTAL_CURRENCY) {
        return period;
      }
      return (period.byCurrency || []).find((item) => item.currency === this.selectedCurrency);
    },
    async initCurrencyOptions() {
      try {
        const { currencies } = await Trainplanet.getOrderSystemOptions();
        this.currencyOptions = Object.keys(currencies || {}).sort(compareCurrencies);
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      }
    },
    updateCharts(statistics) {
      this.rawStats = Object.fromEntries(CHART_GROUPS.map(({ key }) => [key, statistics[key] || []]));

      this.rebuildCharts();
    },
    rebuildCharts() {
      this.chartsVersion += 1;

      this.charts = Object.fromEntries(
        CHART_GROUPS.map((group) => [
          group.key,
          this.buildMetricCharts(group, this.buildChartSeries(this.rawStats[group.key], group)),
        ])
      );
    },
    buildMetricCharts({ key, label, datetime }, { categories, series }) {
      // No periods (e.g. a currency without orders): the template shows a message instead of empty charts
      if (!categories.length) return [];

      // One chart per metric so each gets its own y-axis scale;
      // charts in the same group share a synced crosshair/tooltip.
      // Datetime axis lets ApexCharts thin out the labels on long daily and hourly ranges.
      return series.map((metric, index) => {
        const { color, decimals, isAmount } = METRICS[metric.name];
        const scale = yAxisScale(metric.data, decimals === 0);
        // Total amounts come converted to SEK from the backend
        const currency = this.selectedCurrency === TOTAL_CURRENCY ? "SEK" : this.selectedCurrency;
        const formatTooltipValue = (val) => formatNumber(val, decimals, isAmount ? currency : "");
        // Downloads are named after the chart (e.g. "Total Order Price - Daily - EUR") instead of its internal id
        const filename = [
          metric.name,
          label,
          this.selectedCurrency === TOTAL_CURRENCY ? "Total" : this.selectedCurrency,
        ].join(" - ");

        return {
          name: metric.name,
          series: [metric],
          options: {
            chart: {
              id: `${key}-${index}-v${this.chartsVersion}`,
              group: key,
              type: "line",
              // The draw-in animation gets very slow on long series (a tab of 4 charts took ~1s at 744 points,
              // 17s at 1100), which filled hourly ranges easily reach; kept up to a year of days
              animations: { enabled: categories.length <= 366 },
              toolbar: {
                export: {
                  csv: {
                    filename,
                    // Datetime points are UTC timestamps; ApexCharts' default prints them in local time
                    // (e.g. "Tue Oct 13 2026", a day early west of UTC)
                    ...(datetime && {
                      dateFormatter: (timestamp) => moment.utc(timestamp).format(datetime.exportFormat),
                    }),
                  },
                  png: { filename },
                  svg: { filename },
                },
              },
            },
            colors: [color],
            dataLabels: {
              enabled: false,
            },
            stroke: {
              width: 2,
              curve: "straight",
            },
            markers: {
              // Hide per-point markers on dense ranges; they still show on hover. Not on a line with gaps (values
              // missing from the response): a point between two gaps has no line to it and would only show as its marker.
              size: categories.length > 31 && !metric.data.includes(null) ? 0 : 4,
              hover: { size: 5 },
            },
            xaxis: {
              type: datetime ? "datetime" : "category",
              categories,
              // The tooltip header already shows the period; the extra box under the axis only repeated it
              tooltip: { enabled: false },
            },
            yaxis: {
              min: 0,
              max: scale.max,
              tickAmount: scale.steps,
              // Same label width on every chart keeps the synced plot areas aligned
              labels: { minWidth: 60, formatter: (val) => formatNumber(val, scale.decimals) },
            },
            tooltip: {
              x: datetime ? { format: datetime.tooltipFormat } : {},
              y: { formatter: formatTooltipValue },
              // ApexCharts hides a value when parseFloat(formatted) == 0, which is true for sv-SE decimals
              // like "0,08" (and for real zeros); each chart has a single series, so always show it
              hideEmptySeries: false,
            },
          },
        };
      });
    },
    async submitFilters() {
      this.filter.page = 1;
      await this.initStatistics();
    },
    async onTableOptionsUpdate(options) {
      this.filter = options;
      if (!this.tableOptionsReady) {
        this.tableOptionsReady = true;
        return;
      }
      await this.fetchOrders();
    },
    // A new query: summary, charts and the first page of orders
    async initStatistics() {
      this.loading = true;

      const filters = {
        fromDate: moment(this.fromDate).utc().format(),
        untilDate: moment(this.untilDate).utc().format(),
        tenantIds: [...this.selectedValues],
        inquiry_event: this.event.status,
        // Hourly, daily and monthly periods (and how much of each the range covers) are grouped in Swedish time
        timezone: SWEDISH_TIMEZONE,
      };
      this.pendingFilters = filters;
      const requestId = ++this.latestRequestId;
      // Sent without a currency: the backend would filter the summary and charts by it too, not just the orders.
      // So its order list is only used when no currency is selected; otherwise the list has its own request.
      let ordersRequestId = null;
      if (this.selectedCurrency === TOTAL_CURRENCY) {
        ordersRequestId = ++this.latestOrdersRequestId;
      } else {
        this.fetchOrders();
      }

      try {
        const statistics = await Trainplanet.getStatistics({ ...filters, ...this.pageParams });
        // Unless a page or currency change has asked for a newer list meanwhile
        if (ordersRequestId === this.latestOrdersRequestId) this.orders = normalizeOrders(statistics.orders);
        // A newer query was sent meanwhile; only its response may update the page, otherwise a slow older response
        // could overwrite it and mix two queries on screen
        if (requestId !== this.latestRequestId) return;
        this.appliedFilters = filters;
        this.$store.commit("statistics/setStatistics", statistics);
        this.hasLoaded = true;
        this.updateCharts(this.statistics);
      } catch (error) {
        if (requestId !== this.latestRequestId) return;
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        // Only the latest request settles the page; an older one finishing first must not end the loading state
        // early. Whatever the outcome, the submitted query is no longer loading.
        if (ordersRequestId === this.latestOrdersRequestId) this.tableLoading = false;
        if (requestId === this.latestRequestId) {
          this.pendingFilters = null;
          this.loading = false;
        }
      }
    },
    // The order list alone, for page and currency changes; the summary and charts are left as they are
    async fetchOrders() {
      // Re-runs the query still loading, else the one on screen, so a page change never pulls in
      // edited-but-unsubmitted filters and the table, tiles and charts always describe the same query
      const filters = this.pendingFilters || this.appliedFilters;
      if (!filters) return;

      this.tableLoading = true;
      const requestId = ++this.latestOrdersRequestId;

      try {
        const orders = await this.requestOrders(filters, this.selectedCurrency, this.pageParams);
        if (requestId !== this.latestOrdersRequestId) return;
        this.orders = orders;
      } catch (error) {
        if (requestId !== this.latestOrdersRequestId) return;
        // The rows on screen belong to the previous page or currency; keeping them would show them under the new one
        this.orders = normalizeOrders(null);
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        if (requestId === this.latestOrdersRequestId) this.tableLoading = false;
      }
    },
    // Excel/CSV export: every order of the query and currency on screen
    async fetchAllOrders() {
      // Disabled and loading buttons still let clicks through to export-excel's wrapper, which starts the export,
      // so this also keeps it to one export at a time
      if (this.exportingOrders || !this.canExportOrders) return [];

      this.exportingOrders = true;
      // Fixed at the click: the library reads the file name only after this returns, and a currency change or a new
      // query meanwhile must neither rename the file nor change what the second request below asks for
      const filters = this.appliedFilters;
      const currency = this.selectedCurrency;
      this.orderExportName = buildOrderExportName(filters, currency);

      try {
        let orders = await this.requestOrders(filters, currency, { limit: this.orders.total, offset: 0 });
        // The list can grow after it loaded (ranges that include today); without this the oldest orders would drop out
        if (orders.items.length < orders.total) {
          orders = await this.requestOrders(filters, currency, { limit: orders.total, offset: 0 });
        }
        // Still short (e.g. the backend caps the limit): no file rather than one that silently misses orders
        if (orders.items.length < orders.total) {
          await this.$store.dispatch(
            "error",
            `Only ${orders.items.length} of ${orders.total} orders could be loaded, so nothing was exported.`
          );
          return [];
        }
        return orders.items;
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
        return [];
      } finally {
        this.exportingOrders = false;
      }
    },
    async requestOrders(filters, currency, { limit, offset }) {
      const params = { ...filters, limit, offset };
      // "Total" sends no currency, so orders in every currency are listed
      if (currency !== TOTAL_CURRENCY) params.currencies = [currency];
      // The response's summary and charts are filtered by the currency, so only its order list is used
      const { orders } = await Trainplanet.getStatistics(params);
      return normalizeOrders(orders);
    },
    async downloadAccountingReport() {
      this.loading = true;
      await this.$store.dispatch("loading", true);

      let data = null;
      const toraFiles = this.$refs.fileUploaderTora?.uploadedFiles || [];
      const silverRailFiles = this.$refs.fileUploaderSilverRail?.uploadedFiles || [];
      const swishFiles = this.$refs.fileUploaderSwish?.uploadedFiles || [];

      if (toraFiles.length || silverRailFiles.length || swishFiles.length) {
        data = new FormData();
        if (toraFiles.length) data.append("toraFile", toraFiles[0]);
        if (silverRailFiles.length) data.append("silverRailFile", silverRailFiles[0]);
        if (swishFiles.length) data.append("swishFile", swishFiles[0]);
      }

      const params = {
        fromDate: moment(this.fromDate).utc().format(),
        untilDate: moment(this.untilDate).utc().format(),
        tenantIds: this.selectedValues,
        matchWithEPS: swishFiles.length > 0 || this.matchWithKustom,
      };

      const a = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display: none";

      try {
        const response = await Trainplanet.getStatisticsExcel(data, params);

        const objectUrl = URL.createObjectURL(response.data);
        a.href = objectUrl;
        // Falls back to a generic name if the header is missing (or not exposed to the browser via CORS)
        a.download = (response.headers["content-disposition"] || "").split('"')[1] || "accounting-report.xlsx";
        a.click();
        window.URL.revokeObjectURL(objectUrl);
      } catch (error) {
        let message;

        if (error.response) {
          try {
            message = JSON.parse(await error.response.data.text()).message;
          } catch {
            // Not a JSON body (e.g. a gateway's HTML error page): the generic error below is shown instead
          }
        }

        await this.$store.dispatch("error", message || error);
      } finally {
        this.loading = false;
        await this.$store.dispatch("loading", false);
      }
    },
    selectOnlyTenant(tenantId) {
      this.selectedValues = [tenantId];
    },
    applyDatePreset(preset) {
      const { fromDate, untilDate } = datePresetRange(preset);
      this.fromDate = fromDate;
      this.untilDate = untilDate;
    },
  },
};
</script>

<style lang="scss">
.statistic {
  &-header {
    display: flex !important;
    justify-content: space-between;
  }
}
</style>

<style lang="scss" scoped>
// Disabled buttons keep their own color, faded, instead of Vuetify's grey
.v-btn.theme--dark.v-btn--disabled.v-btn--has-bg {
  opacity: 0.5;

  &.accent {
    background-color: #507dbc !important;
  }

  &.greeny {
    background-color: #86c589 !important;
  }
}

// Long label: wrap onto two lines instead of overflowing on narrower screens
.wrapping-btn {
  white-space: normal;

  ::v-deep .v-btn__content {
    flex: 1 1 auto;
    min-width: 0;
  }
}

// Narrow column: keep the chip and "(+N)" on one line, truncating long tenant names
.tenant-select {
  ::v-deep .v-select__selections {
    flex-wrap: nowrap;
  }

  ::v-deep .v-chip {
    flex-shrink: 1;
    min-width: 0;
  }
}

// Selected currency: solid primary like the selected quick range chip; Vuetify's active overlay would lighten it
.currency-toggle .v-btn.v-btn--active::before {
  opacity: 0;
}

.tenant-only-action {
  visibility: hidden;
}

.v-list-item:hover .tenant-only-action,
.v-list-item--highlighted .tenant-only-action {
  visibility: visible;
}

// Mirror a summary tile line by line, so the tiles keep their height when the values load:
// label (22px line), value (32px line between my-1 margins), caption (20px line).
// Flex stops the bone margins from collapsing into each other.
.summary-skeleton {
  display: flex;
  flex-direction: column;

  ::v-deep .v-skeleton-loader__text {
    width: 40%;
    margin: 5px 0;

    &:last-child {
      margin: 4px 0;
    }
  }

  ::v-deep .v-skeleton-loader__heading {
    width: 30%;
    height: 28px;
    margin: 6px 0;
  }
}

// Mirror a chart cell: title (22px line) above the chart, which ApexCharts makes 265px tall
// (the 250px height plus its default 15px parentHeightOffset)
.chart-skeleton {
  display: flex;
  flex-direction: column;

  ::v-deep .v-skeleton-loader__heading {
    width: 40%;
    height: 16px;
    margin: 3px 0;
  }

  ::v-deep .v-skeleton-loader__image {
    height: 265px;
  }
}
</style>
